import { type UserData } from '@beat-venues/common-types';
import { Container, Heading, Spinner } from '@beat-venues/ui/react';
import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { User } from '../../components/user/user';
import { UserContext } from '../../context';
import { useAnalytics } from '../../hooks/use-analytics';
import { putUserData } from '../../services';
import {
    getCookie,
    mapObjectByKeys,
    mapUserToAbbrev,
    mapUserToAccountForm,
} from '../../utils';

export const Account = () => {
    const { t } = useTranslation('');
    const { user, getUser, isPending } = useContext(UserContext);
    const token = getCookie('token');

    const mutation = useMutation({
        mutationFn: ({
            token,
            userDetails,
        }: {
            token: string;
            userDetails: UserData;
        }) => putUserData(token, userDetails).then(() => getUser()),
    });

    const USER_KEY_ORDER = [
        'email',
        'firstName',
        'lastName',
        'language',
        'mobilePhone',
        'gender',
        'birthdate',
        'country',
        'city',
        'postalCode',
        'street',
    ] as (keyof UserData)[];

    const { triggerPageView } = useAnalytics();

    useEffect(() => {
        triggerPageView();
    }, []);

    if (isPending) return <Spinner className="mb-16" venueId="beat" />;

    return (
        <Container maxWidth="lg">
            {typeof user !== 'undefined' && Object.keys(user).length > 0 ? (
                <User
                    isError={mutation.isError}
                    isPending={mutation.isPending}
                    isSuccess={mutation.isSuccess}
                    title={t('account.account') || ''}
                    updateUser={(userDetails) => {
                        if (token) {
                            mutation.mutate({ token, userDetails });
                        }
                    }}
                    user={mapObjectByKeys<UserData>(
                        mapUserToAccountForm(user as UserData),
                        USER_KEY_ORDER
                    )}
                    userAbbrev={mapUserToAbbrev(user as UserData)}
                />
            ) : (
                <Heading as="h2">{t('error.getUser')}</Heading>
            )}
        </Container>
    );
};
